/* eslint-disable @typescript-eslint/no-unused-expressions */
import { useState } from 'react';
import { Form } from 'formik';
import { FormField } from '@gokrew/ui/form/FormField';
import { Eye, EyeOff } from 'lucide-react';
import { KollabHRLogo } from '@gokrew/ui/assets';
import toast from 'react-hot-toast';
import { useMutation } from '@tanstack/react-query';
import type { ResetPasswordForm } from '@gokrew/models/auth';
import { resetPassword } from '@/api/auth';
import { resetTemporaryPasswordSchema } from '@gokrew/ui/validation/authSchema';
import { useEmployeePortalAuth } from '@/context/EmployeeAuthContext';
import { useTranslation } from 'react-i18next';
import { TranslationNS } from '@gokrew/models/constants';
import { FormikWithSchema } from '@gokrew/ui/form';
import { useDialog } from '@gokrew/ui/context';
import type { DialogComponentProps } from '@gokrew/models/dialog';
import type { ApiResponse } from '@gokrew/models/generic';
import { PasswordValidationIndicator } from '@gokrew/ui/components/PasswordValidationIndicator';
import { Button } from '@/components/common/CustomButton';

interface ResetPasswordFormikValues extends ResetPasswordForm {
  confirmPassword: string;
}

interface ResetPasswordPageProps extends DialogComponentProps {
  dialogMode?: boolean;
}

export function ResetPasswordPage({
  dialogMode = false,
  dialogId,
}: ResetPasswordPageProps) {
  const [showPass, setShowPass] = useState(false);
  const [showNewPass, setShowNewPass] = useState(false);
  const [showConfirmPass, setShowConfirmPass] = useState(false);
  const { fetchUser } = useEmployeePortalAuth();
  const { t } = useTranslation(TranslationNS.ResetPassword);
  const { closeDialog } = useDialog();

  const initialValues: ResetPasswordFormikValues = {
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  };

  /*Reset Password Mutation */
  const resetMutation = useMutation<ApiResponse, Error, ResetPasswordForm>({
    mutationFn: (payload) => resetPassword(payload),
    onSuccess: async () => {
      await fetchUser();
      dialogId && closeDialog(dialogId);
      toast.success(t('toast.success'));
    },
  });

  return (
    <div
      className={
        dialogMode
          ? 'p-2'
          : 'min-h-screen w-full flex items-center justify-center'
      }
    >
      <div className="w-full max-w-md">
        {/* Header */}
        <div className="text-center mb-4 ">
          <div className="flex justify-center w-full">
            <img className="w-15 mb-2" src={KollabHRLogo} alt="Logo" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 ">
            {t('form.change_password')}
          </h2>
          <p className="text-gray-600">{t('form.change_password_message')}</p>
        </div>

        <FormikWithSchema
          initialValues={initialValues}
          validationSchema={resetTemporaryPasswordSchema}
          onSubmit={(values, { setSubmitting }) => {
            resetMutation.mutate(values, {
              onSettled: () => setSubmitting(false),
            });
          }}
        >
          {({ values, isValid, dirty, isSubmitting }) => (
            <Form>
              {/*Temporary password */}
              <FormField
                name="currentPassword"
                type={showPass ? 'text' : 'password'}
                label={t('form.label.temporary_password')}
                placeholder={t('form.placeholder.temporary_password')}
                IconSuffix={showPass ? Eye : EyeOff}
                onIconClick={() => setShowPass(!showPass)}
              />
              {/*New Password */}
              <FormField
                name="newPassword"
                type={showNewPass ? 'text' : 'password'}
                label={t('form.label.new_password')}
                placeholder={t('form.placeholder.new_password')}
                IconSuffix={showNewPass ? Eye : EyeOff}
                onIconClick={() => setShowNewPass(!showNewPass)}
              />
              {/*Confirm New Password */}
              <FormField
                name="confirmPassword"
                type={showConfirmPass ? 'text' : 'password'}
                label={t('form.label.confirm_password')}
                placeholder={t('form.placeholder.confirm_password')}
                IconSuffix={showConfirmPass ? Eye : EyeOff}
                onIconClick={() => setShowConfirmPass(!showConfirmPass)}
              />

              {/* Password Validation Indicator*/}
              <PasswordValidationIndicator
                password={values.newPassword}
                confirmPassword={values.confirmPassword}
                showMatchValidation={true}
              />

              <Button
                type="submit"
                variant="primary"
                disabled={!dirty || !isValid || isSubmitting}
                className="w-full mt-1.5"
              >
                {t('form.change_password')}
              </Button>
            </Form>
          )}
        </FormikWithSchema>
      </div>
    </div>
  );
}
